import { useEffect, useState, useRef } from 'react';
import { cn } from '@/lib/utils';

interface TextRevealProps {
  children: string;
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span';
  delay?: number;
  staggerDelay?: number;
  animateBy?: 'letter' | 'word';
  once?: boolean;
}

const TextReveal = ({
  children,
  className,
  as: Component = 'span',
  delay = 0,
  staggerDelay = 0.03,
  animateBy = 'word',
  once = true,
}: TextRevealProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [once]);

  const elements = animateBy === 'letter' 
    ? children.split('') 
    : children.split(' ');

  return (
    <Component ref={ref as any} className={cn('inline-block', className)}>
      {elements.map((element, index) => {
        // First element renders immediately visible for LCP, others animate in
        const isFirstElement = index === 0;
        const shouldBeVisible = isFirstElement || isVisible;
        
        return (
          <span
            key={index}
            className="inline-block overflow-hidden py-[0.1em] -my-[0.1em] px-[0.05em] -mx-[0.05em]"
            style={{ marginRight: animateBy === 'word' ? '0.25em' : undefined }}
          >
            <span
              className="inline-block transition-all duration-500"
              style={{
                transform: shouldBeVisible ? 'translateY(0)' : 'translateY(110%)',
                opacity: shouldBeVisible ? 1 : 0,
                transitionDelay: isFirstElement ? '0s' : `${delay + index * staggerDelay}s`,
                transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            >
              {element === ' ' ? '\u00A0' : element}
            </span>
          </span>
        );
      })}
    </Component>
  );
};

export default TextReveal;
